import React, { useContext, useState } from "react";
import { Box, FormControl, IconButton, Typography } from "@mui/material";
import { BaseButton, BaseFormCard, BaseOtpInput } from "../../base";
import CloseIcon from "../../../assets/icons/CloseIcon";
import ValidationMessage from "../../ValidationMessage/ValidationMessage";
import {
  resendCodeSendFail,
  verificationCodeFail,
  verificationCodeSuccess,
  verifyCodeinvalid
} from "./validation_messages";
import { EcAuthContext, ECAxiosAPI } from "@eclear-ag/ec-common-ui";
import { URLS } from "../../../configuration/urls";
import { FormattedMessage } from "react-intl";

const initialFieldState = {
  code: {
    state: "default",
    validateEnabled: false
  }
};

export default function VerificationCode({
  setScreen,
  formData,
  setIsModalVisible,
  onClose
}) {
  const { signin } = useContext(EcAuthContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isValidateVisible, setIsValidateVisible] = useState(false);
  const [validationMessages, setValidationMessages] = useState(
    verificationCodeSuccess
  );
  const [verifyCode, setVerifyCode] = useState("");
  const [fieldState, setFieldState] = useState(initialFieldState);
  const [isPasswordChangeSuccess, setIsPasswordChangeSuccess] = useState(false);

  const submitCode = async () => {
    setIsSubmitting(true);
    await ECAxiosAPI.post(`${URLS.PASSWORD_UPDATE()}`, {
      email: formData.email,
      password: formData.newPassword,
      code: verifyCode
    })
      .then((res) => {
        setIsValidateVisible(true);
        setValidationMessages(verificationCodeSuccess);
        setIsSubmitting(false);
        signin(formData.email, formData.newPassword);
        setFieldState(initialFieldState);
        setVerifyCode("");
        setIsPasswordChangeSuccess(true);
      })
      .catch((err) => {
        setIsValidateVisible(true);
        setValidationMessages(verificationCodeFail);
        setIsSubmitting(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateCode(verifyCode, true, true);
  };

  const resendVerifyCode = async () => {
    setIsResending(true);
    await ECAxiosAPI.post(`${URLS.REQUEST_PASSWORD_UPDATING()}`, {
      email: formData.email,
      password: formData.currentPassword
    })
      .then((res) => {
        setIsResending(false);
      })
      .catch((err) => {
        setValidationMessages(resendCodeSendFail);
        setIsValidateVisible(true);
        setIsResending(false);
      });
  };

  const updateFieldState = (updates) => {
    setFieldState((prevState) => {
      const newState = { ...prevState };
      Object.keys(updates).forEach((field) => {
        if (newState[field]) {
          newState[field] = {
            ...newState[field],
            ...updates[field]
          };
        }
      });
      return newState;
    });
  };

  const validateCode = (val, forceEnableValidate, submitForm) => {
    if (fieldState.code.validateEnabled || forceEnableValidate === true) {
      switch (true) {
        case val.length < 6:
          updateFieldState({
            code: {
              state: "error",
              validateEnabled: true
            }
          });
          setValidationMessages(verifyCodeinvalid);
          setIsValidateVisible(true);
          break;
        case val.length === 6:
          updateFieldState({
            code: {
              state: "default",
              validateEnabled: true
            }
          });
          setIsValidateVisible(false);
          submitForm && submitCode();
          break;
        default:
          break;
      }
    }
  };

  const closeModal = onClose || (() => setIsModalVisible(false));

  return (
    <BaseFormCard
      data-testid="verification-code-card"
      title="Change Password"
      headerExtra={
        <IconButton
          onClick={closeModal}
          sx={{ ml: "auto", p: "4px" }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      }
      onSubmit={handleSubmit}
      footerJustify={isPasswordChangeSuccess ? "flex-end" : "space-between"}
      footer={
        !isPasswordChangeSuccess ? (
          <>
            <BaseButton
              data-testid="resend-verification-code"
              variant="text"
              loading={isResending}
              loadingTestId="resend-verification-loading"
              disabled={isSubmitting}
              onClick={() => {
                resendVerifyCode();
              }}
              sx={{ height: "auto", minWidth: 0 }}
            >
              <FormattedMessage id="Resend Verification Code" />
            </BaseButton>
            <BaseButton
              data-testid="confirm-verification-button"
              type="submit"
              variant="filled"
              loading={isSubmitting}
              loadingTestId="confirm-verification-loading"
              disabled={isResending}
            >
              <FormattedMessage id="Confirm Verification" />
            </BaseButton>
          </>
        ) : (
          <BaseButton
            data-testid="verification-close-button"
            onClick={closeModal}
            variant="filled"
          >
            <FormattedMessage id="Close" />
          </BaseButton>
        )
      }
    >
      {!isPasswordChangeSuccess && (
        <Box
          sx={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <FormControl
            required
            sx={{ display: "flex", flexDirection: "column", gap: "6px" }}
          >
            <Typography
              component="label"
              htmlFor="verification-code"
              sx={{ p: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.57 }}
            >
              <FormattedMessage id="verify.fieldTitle" />
            </Typography>
            <Typography
              component="p"
              sx={{ m: 0, fontSize: 14, fontWeight: 400, lineHeight: 1.57 }}
            >
              <FormattedMessage id="verify.description" />
            </Typography>
            <BaseOtpInput
              data-testid="verification-code-input"
              length={6}
              placeholder="-"
              passwordMask="-"
              onCompleted={(code) => {
                const verifyCode = code.replaceAll("-", "");
                if (verifyCode.length > 5) {
                  setVerifyCode(verifyCode);
                }
                validateCode(verifyCode);
              }}
            />
          </FormControl>
          <ValidationMessage
            visible={isValidateVisible}
            setVisible={setIsValidateVisible}
            messages={validationMessages}
          />
        </Box>
      )}
      {isPasswordChangeSuccess && (
        <Box sx={{ padding: "16px" }}>
          <ValidationMessage
            closeButton={false}
            visible={isValidateVisible}
            setVisible={setIsValidateVisible}
            messages={validationMessages}
          />
        </Box>
      )}
    </BaseFormCard>
  );
}
